import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Props {
  connected: boolean;
  onConnect: (apiKey: string) => Promise<unknown>;
  onDisconnect: () => Promise<unknown>;
  disabled?: boolean;
}

export function ConnectZernioCard({ connected, onConnect, onDisconnect, disabled }: Props) {
  const { t } = useTranslation("settings");
  const [apiKey, setApiKey] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleConnect() {
    if (!apiKey.trim()) return;
    setBusy(true);
    try {
      await onConnect(apiKey.trim());
      toast.success(t("zernio.connected"));
      setApiKey("");
      setShowInput(false);
    } catch (e: any) {
      toast.error(e.message ?? t("zernio.failedConnect"));
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm(t("zernio.disconnectConfirm"))) return;
    setBusy(true);
    try {
      await onDisconnect();
      toast.success(t("zernio.disconnected"));
    } catch (e: any) {
      toast.error(e.message ?? t("zernio.failedDisconnect"));
    } finally {
      setBusy(false);
    }
  }

  if (connected && !showInput) {
    return (
      <div className="rounded-lg border p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-sm font-medium">{t("zernio.connectedStatus")}</span>
        </div>
        <div className="flex gap-3 text-sm">
          <button className="underline hover:no-underline" onClick={() => setShowInput(true)} disabled={disabled}>{t("zernio.replaceKey")}</button>
          <button className="text-destructive underline hover:no-underline" onClick={handleDisconnect} disabled={disabled || busy}>{t("zernio.disconnect")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <h3 className="text-base font-semibold">{connected ? t("zernio.replaceTitle") : t("zernio.connectTitle")}</h3>
      <p className="text-sm text-muted-foreground">{t("zernio.apiKeyHint")}</p>
      <input
        type="password"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder={t("zernio.apiKeyPlaceholder")}
        className="block w-full rounded border px-3 py-2 text-sm font-mono"
        disabled={disabled || busy}
      />
      <div className="flex justify-end gap-2">
        {connected && <Button variant="outline" onClick={() => setShowInput(false)}>{t("actions.cancel", { ns: "common" })}</Button>}
        <Button onClick={handleConnect} disabled={!apiKey.trim() || disabled || busy}>
          {busy ? t("zernio.connecting") : connected ? t("zernio.replace") : t("zernio.connect")}
        </Button>
      </div>
    </div>
  );
}
